import { io, Socket } from 'socket.io-client';

// Types for Socket events
export interface LocationUpdate {
  riderId: string;
  latitude: number;
  longitude: number;
  timestamp: string;
  speed?: number;
  heading?: number;
}

export interface DeliveryStatusUpdate {
  orderId: string;
  status: 'pending' | 'picked_up' | 'in_transit' | 'delivered' | 'cancelled' | 'failed';
  driverId: string;
  timestamp: string;
  location?: {
    latitude: number;
    longitude: number;
  };
  notes?: string;
}

export interface OrderStatusUpdate {
  orderId: string;
  status: string;
  timestamp: string;
  updatedBy: string;
  notes?: string;
}

export interface NotificationData {
  id: string;
  type: 'order' | 'delivery' | 'system' | 'alert';
  title: string;
  message: string;
  timestamp: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  data?: any;
}

class SocketService {
  private socket: Socket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000; // Start with 1 second

  /**
   * Initialize socket connection
   */
  connect(token?: string): Socket {
    if (this.socket?.connected) {
      console.log('Socket already connected');
      return this.socket;
    }

    const apiUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000/api';
    const socketUrl = apiUrl.replace('/api', ''); // Remove /api for socket connection

    console.log('Connecting to Socket.IO server:', socketUrl);

    this.socket = io(socketUrl, {
      auth: {
        token: token || localStorage.getItem('token'),
      },
      // Try polling first, then upgrade to websocket
      transports: ['polling', 'websocket'],
      reconnection: true,
      reconnectionAttempts: this.maxReconnectAttempts,
      reconnectionDelay: this.reconnectDelay,
      // Add timeout and upgrade settings
      timeout: 10000,
      upgrade: true,
      rememberUpgrade: true,
      // Add path if backend uses custom path
      path: '/socket.io/',
    });

    this.setupEventListeners();
    return this.socket;
  }

  /**
   * Setup default event listeners
   */
  private setupEventListeners(): void {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      console.log('Socket connected:', this.socket?.id);
      this.reconnectAttempts = 0;
    });

    this.socket.on('disconnect', (reason) => {
      console.log('Socket disconnected:', reason);
    });

    this.socket.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
      this.reconnectAttempts++;
      
      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        console.error('Max reconnection attempts reached');
      }
    });

    this.socket.on('error', (error) => {
      console.error('Socket error:', error);
    });

    this.socket.on('reconnect', (attemptNumber) => {
      console.log('Socket reconnected after', attemptNumber, 'attempts');
      this.reconnectAttempts = 0;
    });

    this.socket.on('reconnect_attempt', (attemptNumber) => {
      console.log('Attempting to reconnect...', attemptNumber);
    });

    this.socket.on('reconnect_failed', () => {
      console.error('Socket reconnection failed');
    });
  }

  /**
   * Disconnect socket
   */
  disconnect(): void {
    if (this.socket) {
      console.log('Disconnecting socket');
      this.socket.disconnect();
      this.socket = null;
    }
  }

  /**
   * Check if socket is connected
   */
  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  /**
   * Get socket instance
   */
  getSocket(): Socket | null {
    return this.socket;
  }

  // =====================
  // Location Tracking
  // =====================

  /**
   * Subscribe to driver location updates
   */
  onLocationUpdate(callback: (data: LocationUpdate) => void): void {
    this.socket?.on('location:update', callback);
  }

  /**
   * Unsubscribe from driver location updates
   */
  offLocationUpdate(callback: (data: LocationUpdate) => void): void {
    this.socket?.off('location:update', callback);
  }

  /**
   * Emit driver location (for driver app)
   */
  emitLocation(locationData: LocationUpdate): void {
    this.socket?.emit('location:update', locationData);
  }

  /**
   * Subscribe to specific rider's location
   */
  subscribeToRider(riderId: string): void {
    console.log('Subscribing to rider:', riderId);
    this.socket?.emit('rider:subscribe', { riderId });
  }

  /**
   * Unsubscribe from specific rider's location
   */
  unsubscribeFromRider(riderId: string): void {
    console.log('Unsubscribing from rider:', riderId);
    this.socket?.emit('rider:unsubscribe', { riderId });
  }

  // =====================
  // Delivery Status
  // =====================

  /**
   * Subscribe to delivery status updates
   */
  onDeliveryStatusUpdate(callback: (data: DeliveryStatusUpdate) => void): void {
    this.socket?.on('delivery:status', callback);
  }

  /**
   * Unsubscribe from delivery status updates
   */
  offDeliveryStatusUpdate(callback: (data: DeliveryStatusUpdate) => void): void {
    this.socket?.off('delivery:status', callback);
  }

  /**
   * Emit delivery status update (for driver app)
   */
  emitDeliveryStatus(statusData: DeliveryStatusUpdate): void {
    this.socket?.emit('delivery:status', statusData);
  }

  /**
   * Subscribe to specific order's delivery updates
   */
  subscribeToOrder(orderId: string): void {
    console.log('Subscribing to order:', orderId);
    this.socket?.emit('order:subscribe', { orderId });
  }

  /**
   * Unsubscribe from specific order's delivery updates
   */
  unsubscribeFromOrder(orderId: string): void {
    console.log('Unsubscribing from order:', orderId);
    this.socket?.emit('order:unsubscribe', { orderId });
  }

  // =====================
  // Order Status
  // =====================

  /**
   * Subscribe to order status updates
   */
  onOrderStatusUpdate(callback: (data: OrderStatusUpdate) => void): void {
    this.socket?.on('order:status', callback);
  }

  /**
   * Unsubscribe from order status updates
   */
  offOrderStatusUpdate(callback: (data: OrderStatusUpdate) => void): void {
    this.socket?.off('order:status', callback);
  }

  /**
   * Emit order status update
   */
  emitOrderStatus(statusData: OrderStatusUpdate): void {
    this.socket?.emit('order:status', statusData);
  }

  // =====================
  // Notifications
  // =====================

  /**
   * Subscribe to notifications
   */
  onNotification(callback: (data: NotificationData) => void): void {
    this.socket?.on('notification', callback);
  }

  /**
   * Unsubscribe from notifications
   */
  offNotification(callback: (data: NotificationData) => void): void {
    this.socket?.off('notification', callback);
  }

  /**
   * Mark notification as read
   */
  markNotificationRead(notificationId: string): void {
    this.socket?.emit('notification:read', { notificationId });
  }

  // =====================
  // Room Management
  // =====================

  /**
   * Join a room (for admin/pharmacy to monitor specific drivers/orders)
   */
  joinRoom(room: string): void {
    console.log('Joining room:', room);
    // Backend expects 'join' event with room string directly
    this.socket?.emit('join', room);
  }

  /**
   * Leave a room
   */
  leaveRoom(room: string): void {
    console.log('Leaving room:', room);
    // Backend expects 'leave' event with room string directly
    this.socket?.emit('leave', room);
  }

  // =====================
  // Custom Events
  // =====================

  /**
   * Emit a custom event
   */
  emit(event: string, data: any): void {
    this.socket?.emit(event, data);
  }

  /**
   * Subscribe to a custom event
   */
  on(event: string, callback: (data: any) => void): void {
    this.socket?.on(event, callback);
  }

  /**
   * Unsubscribe from a custom event
   */
  off(event: string, callback?: (data: any) => void): void {
    if (callback) {
      this.socket?.off(event, callback);
    } else {
      this.socket?.off(event);
    }
  }
}

// Create and export a singleton instance
const socketService = new SocketService();

export default socketService;

